import { forwardRef, memo, ReactNode, HTMLAttributes } from "react";
import { Tooltip } from "@mui/material";
import SysColumn from "app/class/sys-column";
import SysTable from "app/class/sys-table";
import Translate from "component/global/translation/translation-component";
import { useSession } from "app/hooks/useSession";
import { formatDateTimeValue, formatDateValue, formatRelativeTimeFromNow, formatTimeValue, getUserDateTimePreferences } from "modules/record/shared/services/dateTimePreferences";
import { formatCurrencyValue } from "modules/record/shared/services/currencyPreferences";
import { PASSWORD_MASK, hasStoredPasswordValue } from "modules/record/shared/services/passwordField";
import { resolveDisplayText } from "modules/record/shared/services/recordMetadataLabels";
import { useCurrencyPreferences } from "modules/record/shared/hooks/useCurrencyPreferences";
import { useTranslation } from "react-i18next";
import { formatConditionSummary, resolveConditionColumns } from "../services/conditionSummary";

import ThumbUpOffAltIcon from '@mui/icons-material/ThumbUpOffAlt';
import ThumbDownOffAltIcon from '@mui/icons-material/ThumbDownOffAlt';

interface RecordListCellRendererProps {
    column: SysColumn;
    value: any;
    row?: Record<string, any>;
    choiceLabelMap?: Map<string, string>;
    currencyCodeOverride?: string | null;
    tableColumns?: SysColumn[];
    choiceLabelMaps?: Record<string, Map<string, string>>;
    allColumns?: SysColumn[];
    tables?: SysTable[];
}

const CellText = forwardRef<HTMLSpanElement, { value: string; children?: ReactNode } & HTMLAttributes<HTMLSpanElement>>(({ value, children, style, ...props }, ref) => (
    <span
        {...props}
        ref={ref}
        title={value}
        style={{ display: "block", width: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", ...style }}
    >
        {children ?? value}
    </span>
));

const getReferenceDisplayValue = (value: unknown) => {

    if (!value || typeof value !== "object" || Array.isArray(value)) {
        return null;
    }

    const candidate = value as Record<string, unknown>;

    if (typeof candidate.display === "string" && candidate.display) {
        return candidate.display;
    }

    if (typeof candidate.label === "string" && candidate.label) {
        return candidate.label;
    }

    if (typeof candidate.name === "string" && candidate.name) {
        return candidate.name;
    }

    if (typeof candidate.id === "string" && candidate.id) {
        return candidate.id;
    }

    return null;
};

const RecordListCellRendererComponent = memo(
    ({ column, value, row, choiceLabelMap, currencyCodeOverride, tableColumns = [], choiceLabelMaps = {}, allColumns = [], tables = [] }: RecordListCellRendererProps) => {
        const { data } = useSession();
        const { t, i18n } = useTranslation();
        const currencyPreferences = useCurrencyPreferences({ enabled: column.column_type === "CURRENCY" });
        const dateTimePreferences = getUserDateTimePreferences(data);
        const language = i18n.resolvedLanguage || i18n.language || data?.preferredLanguage || "en";
        const concreteTableId = tables.find(table => table.name === row?.sys_class_name)?.id;

        if (value == null && column.column_type !== "PASSWORD") {
            return <Translate text="Empty" sx={{ color: "var(--prm-text-message)", userSelect: "none" }} />;
        }

        if (column.name === "sys_class_name") {
            return <CellText value={resolveDisplayText(t, String(row?.sys_class_label || value))} />;
        }

        switch (column.column_type) {
            case "CONDITION":
                return <CellText value={formatConditionSummary(value, resolveConditionColumns(column, row || {}, tableColumns, allColumns, tables), t, choiceLabelMaps, tables, allColumns)} />;
            case "PASSWORD":
                return hasStoredPasswordValue(column.name, value, row)
                    ? <CellText value={PASSWORD_MASK} />
                    : <Translate text="Empty" sx={{ color: "var(--prm-text-message)", userSelect: "none" }} />;

            case "BOOLEAN":
                return <span aria-label={String(t(value ? "Yes" : "No"))}>{value ? <ThumbUpOffAltIcon aria-hidden="true" color="success" /> : <ThumbDownOffAltIcon aria-hidden="true" color="error" />}</span>;

            case "DATE":
                return (
                    <Tooltip title={formatRelativeTimeFromNow(value, { language, dateOnly: true })}>
                        <CellText value={formatDateValue(value, dateTimePreferences)} />
                    </Tooltip>
                );
            case "DATETIME":
                return (
                    <Tooltip title={formatRelativeTimeFromNow(value, { language })}>
                        <CellText value={formatDateTimeValue(value, dateTimePreferences)} />
                    </Tooltip>
                );
            case "TIME":
                return <CellText value={formatTimeValue(value, dateTimePreferences)} />;
            case "DECIMAL":
                return <CellText value={Number(value).toLocaleString(data?.preferredLanguage || "en")} />;
            case "CURRENCY":
                return <CellText value={formatCurrencyValue(value, column, data, currencyPreferences.data, currencyCodeOverride)} />;

            case "COLOR_PICKER":
                return (
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                        <span
                            style={{
                                width: 18,
                                height: 18,
                                borderRadius: 4,
                                border: "1px solid var(--prm-border, #e5e7eb)",
                                background: String(value || "transparent"),
                                display: "inline-block",
                            }}
                        />
                        {String(value)}
                    </span>
                );

            case "CODE":
                return <CellText value={typeof value === "string" ? value.trim().replace(/\s+/g, " ") : JSON.stringify(value)} />;

            case "RICH_HTML": {
                const displayValue = String(value).replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
                return <CellText value={displayValue} />;
            }

            case "REFERENCE":
            case "DYNAMIC_REFERENCE": {
                const displayValue = getReferenceDisplayValue(value);
                return <CellText value={resolveDisplayText(t, displayValue ?? String(value))} />;
            }

            case "CHOICE":
                return <CellText value={resolveDisplayText(t, (concreteTableId ? choiceLabelMaps[`${column.id}:${concreteTableId}`] : undefined)?.get(String(value))
                    ?? choiceLabelMap?.get(String(value)) ?? choiceLabelMaps[column.id]?.get(String(value)) ?? String(value))} />;

            default:
                return <CellText value={String(value)} />;
        }
    }
);

export default RecordListCellRendererComponent;
